<template>
  <div :class="`time-interval-picker time-interval-picker_${layout}`">
    <Form
      ref="formRef"
      :rules="formRules"
      :model="state"
      :layout="ELayout.vertical"
    >
      <div
        v-if="state.isTimeInterval"
        class="time-interval-picker__wrapper"
      >
        <FormItem
          name="intervalValue"
          class="time-interval-picker__item"
          :class="{ 'time-interval-picker__item_without-icon': !!state.timeStart }"
        >
          <TimeRangePicker
            popupClassName="time-range-picker"
            :placeholder="[capitalize(tt('shared.since')), capitalize(tt('shared.by'))]"
            :value="[state.timeStart, state.timeEnd]"
            :format="displayFormat"
            :minuteStep="minuteStep"
            :disabled="[disabledStart, disabledEnd]"
            @change="handleRangeTimeChange"
          />
        </FormItem>
      </div>
      <div
        v-else
        class="time-interval-picker__wrapper"
      >
        <FormItem name="exactValue">
          <TimePicker
            popupClassName="time-interval-picker__time-picker time-interval-picker__time-picker_long"
            :value="state.timeStart || undefined"
            :format="displayFormat"
            :placeholder="placeholder"
            :minuteStep="minuteStep"
            :disabled="disabledStart"
            @change="handleExactTimeChange"
            @select="handleExactTimeChange"
          />
        </FormItem>
      </div>
    </Form>
    <Checkbox
      v-if="singleAndIntervalMode"
      :class="`time-interval-picker__checkbox time-interval-picker__checkbox_${layout}`"
      :checked="state.isTimeInterval"
      @change="handleCheckboxChange($event.target.checked)"
    >
      {{ tt('shared.timeInterval') }}
    </Checkbox>
  </div>
</template>

<script lang="ts">
import {
  TimePicker,
  TimeRangePicker,
  Form,
  FormItem,
} from 'ant-design-vue';
import {
  computed,
  defineComponent,
  PropType,
  reactive,
  ref,
  toRaw,
  watch,
} from 'vue';

import { Checkbox } from '@/ui';
import { DATE_FORMAT, ELayout } from '@/constants';
import { EDateTimeType } from '@/domains/constants';
import { TPickerOptions, TDateAppFormat } from '@/domains/types';
import { useFormHandlers } from '@/composables/useFormHandlers';
import tt from '@/i18n/utils/translateText';
import { capitalize } from '@/utils';

import { TTimeRangePickerChangeValue } from '../IntervalPicker/domain/types';
import { EIntervalMode } from '../IntervalPicker/domain/constants';

export default defineComponent({
  name: 'TimeIntervalPicker',
  components: {
    Checkbox,
    TimePicker,
    TimeRangePicker,
    Form,
    FormItem,
  },
  props: {
    time: {
      type: Object as PropType<TPickerOptions>,
      default: null,
    },

    outputFormat: {
      type: String,
      default: DATE_FORMAT.hoursMinutes,
    },

    errors: {
      type: Object,
      default: () => ({}),
    },

    mode: {
      type: String as PropType<EIntervalMode>,
      default: EIntervalMode.singleAndInterval,
    },

    layout: {
      type: String as PropType<ELayout>,
      default: ELayout.vertical,
    },

    disabledStart: {
      type: Boolean,
      default: false,
    },

    disabledEnd: {
      type: Boolean,
      default: false,
    },

    minuteStep: {
      type: Number,
      default: 1,
    },
    placeholder: {
      type: String,
      default: '',
    },
  },
  emits: ['change'],
  setup(props, { emit }) {
    const formRef = ref<typeof Form | null>(null);

    const rules = {
      exactValue: [],
      intervalValue: [],
    };

    const { formRules, onControlChange, onError } = useFormHandlers(formRef, { rules });

    const setError = () => {
      onError({ errors: props.errors });
    };

    watch(() => props.errors, setError);

    const singleAndIntervalMode = computed(() => props.mode === EIntervalMode.singleAndInterval);

    const getInitialIsInterval = (mode: EIntervalMode, isInterval: boolean) => {
      if (mode === EIntervalMode.single) return false;
      if (mode === EIntervalMode.interval) return true;
      return isInterval;
    };

    const state = reactive({
      isTimeInterval: getInitialIsInterval(props.mode, props.time?.interval),
      timeStart: props.time?.start as TDateAppFormat | null,
      timeEnd: props.time?.end as TDateAppFormat | null,
    });

    const setTimeFromProps = () => {
      state.isTimeInterval = getInitialIsInterval(props.mode, props.time?.interval);
      state.timeStart = props.time?.start;
      state.timeEnd = props.time?.end;
    };

    watch(() => [props.time?.start, props.time?.end, props.time?.interval], setTimeFromProps);

    const handleChange = () => {
      const { timeStart, timeEnd } = toRaw(state);
      emit('change', {
        start: timeStart?.format(props.outputFormat),
        end: timeEnd?.format(props.outputFormat),
        type: state.isTimeInterval ? EDateTimeType.interval : EDateTimeType.determined,
      });
    };

    const handleCheckboxChange = (checked: boolean) => {
      state.isTimeInterval = checked;
      handleChange();
    };

    // TODO При замене Antd компонентов на компоненты из UI изменить тип `string | TDateAppFormat` -> `TDateAppFormat | null` (если будет актуально)
    const handleExactTimeChange = (time: string | TDateAppFormat | null) => {
      state.timeStart = time as TDateAppFormat | null;
      onControlChange({ name: 'exactValue' });
      handleChange();
    };

    // TODO При замене Antd компонентов на компоненты из UI изменить тип `TTimeRangePickerChangeValue` -> `(TDateAppFormat | null)[] | null` (если будет актуально)
    const handleRangeTimeChange = (value: TTimeRangePickerChangeValue) => {
      const time = value as (TDateAppFormat | null)[] | null;

      if (!time) {
        state.timeStart = null;
        state.timeEnd = null;
      } else {
        const [startTime, endTime] = time;
        state.timeStart = startTime;
        state.timeEnd = endTime;
      }
      onControlChange({ name: 'intervalValue' });
      handleChange();
    };

    return {
      formRules,
      formRef,
      state,
      displayFormat: DATE_FORMAT.hoursMinutes,
      singleAndIntervalMode,
      ELayout,

      handleCheckboxChange,
      handleExactTimeChange,
      handleRangeTimeChange,
      tt,
      capitalize,
    };
  },
});
</script>

<style lang="scss" src="./styles.scss" />
